import { useState } from 'react'
import styled from 'styled-components'
import { useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { Link, Navigate } from 'react-router-dom'
import { TableRow, TableCell } from '@mui/material'
import { DEFAULT_USER_AVATAR, S3_URL, TM_URL, TOURNAMENT_TYPE } from 'config'
import ErrorMessage from 'components/ui/ErrorMessage'
import LoadingSpinnerGreen from 'components/ui/LoadingSpinnerGreen'
import SingleRoundPlayersModal from './SingleRoundPlayersModal'
import GameLogsModal from './GameLogsModal'
import { ClientRoute } from 'routes/client'
import { selectLocale } from 'store/locale/slice'
import { deleteGame, deleteGameRound, getGameLogs, updateGame } from 'store/game/thunks'
import { clearGameLogs, selectGame } from 'store/game/slice'
import { SimulatorIcon } from 'assets/icons'

enum GAMEOPTIONS {
  Settings = 'settings',
  Players = 'players',
  Close = 'close',
  ReOpen = 're-open',
  Delete = 'delete',
  Logs = 'logs',
}

interface Props {
  round: GameSearchRound
  user?: User
}

export default function SingleRoundRow({ round, user }: Props): JSX.Element {
  const [playersModalOpen, setPlayersModalOpen] = useState<boolean>(false)
  const [logsModalOpen, setLogsModalOpen] = useState<boolean>(false)
  const [redirectTo, setRedirectTo] = useState<string | null>(null)
  const intl = useIntl()
  const dispatch = useDispatch()
  const locale = useSelector(selectLocale)
  const { deletingStatus, deletingError, deletingId, updatingStatus, updatingError, updatingId } =
    useSelector(selectGame)

  const getRoundDate = (round: GameSearchRound) => {
    const date = round.date.date || round.date
    return new Intl.DateTimeFormat(locale.language.code).format(new Date(date))
  }

  const roundIsTmGame = (round: GameSearchRound): boolean => {
    return Boolean(round.tournamentId)
  }

  const roundIsPrivateGame = (round: GameSearchRound): boolean => {
    return round.isPrivate === true
  }

  const roundIsHcpRound = (round: GameSearchRound): boolean => {
    return round.isHcpRound === true
  }

  const roundIsRvbRound = (round: GameSearchRound): boolean => {
    return round.parent?.tournamentType === TOURNAMENT_TYPE.RVB
  }

  const roundIsChildEvent = (round: GameSearchRound): boolean => {
    return Boolean(round.parent)
  }

  const roundIsSimulatorRound = (round: GameSearchRound): boolean => {
    return round.isSimulator === true
  }

  const getRoundName = (round: GameSearchRound) => {
    const isTmGame = roundIsTmGame(round)
    const isPrivateGame = roundIsPrivateGame(round)
    const isHcpRound = roundIsHcpRound(round)
    const isRvbRound = roundIsRvbRound(round)
    const isSimulatorRound = roundIsSimulatorRound(round)

    const badges: RoundBadge[] = [
      { visible: isTmGame, element: <TmBadge key={'tm'}>TM</TmBadge> },
      { visible: isPrivateGame, element: <PrivateBadge key={'private'}>P</PrivateBadge> },
      { visible: isHcpRound, element: <HcpBadge key={'hcp'}>HCP</HcpBadge> },
      { visible: isRvbRound, element: <RvbBadge key={'rvsb'}>RvsB</RvbBadge> },
      { visible: isSimulatorRound, element: <SimulatorBadge key={'simulator'} /> },
    ]

    return (
      <>
        {round.name} {badges.filter((be) => be.visible).map((be) => be.element)}
      </>
    )
  }

  const tmGameOptions = (round: GameSearchRound) => {
    const url = `${TM_URL}tournaments/${round.tournamentId}`
    return (
      <TmLinkButton href={url} target="_blank" rel="noreferrer noopener">
        {intl.formatMessage({ id: 'strings.openInTm' })}
      </TmLinkButton>
    )
  }

  const appGameOptions = (round: GameSearchRound) => {
    let openCloseLabel = intl.formatMessage({ id: 'strings.closeGame' })
    let openCloseValue = GAMEOPTIONS.Close

    if (round.isCompleted) {
      openCloseLabel = intl.formatMessage({ id: 'strings.reOpenGame' })
      openCloseValue = GAMEOPTIONS.ReOpen
    }

    return (
      <GameDropdown onChange={handleAppGameOptionsChange} value="" disabled={roundIsChildEvent(round)}>
        <option disabled={round.isMultiround} value="">
          {intl.formatMessage({ id: 'strings.options' })}
        </option>
        <option disabled={round.isMultiround} value={GAMEOPTIONS.Settings}>
          {intl.formatMessage({ id: 'strings.settings' })}
        </option>
        <option disabled={round.isMultiround} value={GAMEOPTIONS.Players}>
          {intl.formatMessage({ id: 'strings.players' })}
        </option>
        <option disabled value={GAMEOPTIONS.Logs}>
          {intl.formatMessage({ id: 'strings.modificationLog' })}
        </option>
        <option disabled={round.isMultiround} value={openCloseValue}>
          {openCloseLabel}
        </option>
        <option value={GAMEOPTIONS.Delete}>{intl.formatMessage({ id: 'strings.removeGame' })}</option>
      </GameDropdown>
    )
  }

  const handleAppGameOptionsChange = (event) => {
    const { value } = event.target
    const gameId = round.eventId

    if (value === GAMEOPTIONS.Settings) {
      setRedirectTo(ClientRoute.ADMIN_GAME_SETTINGS(gameId))
    }

    if (value === GAMEOPTIONS.Players) {
      setPlayersModalOpen(true)
    }

    const confirmReOpenText = intl.formatMessage({ id: 'strings.roundReOpenConfirmation' })
    if (value === GAMEOPTIONS.ReOpen && window.confirm(confirmReOpenText)) {
      const payload = getUpdateGamePayload(false)
      dispatch(updateGame(payload))
    }

    const confirmCloseText = intl.formatMessage({ id: 'strings.roundCloseConfirmation' })
    if (value === GAMEOPTIONS.Close && window.confirm(confirmCloseText)) {
      const payload = getUpdateGamePayload(true)
      dispatch(updateGame(payload))
    }

    const confirmDeleteText = intl.formatMessage({ id: 'strings.deleteGameConfirmation' })
    if (value === GAMEOPTIONS.Delete && window.confirm(confirmDeleteText)) {
      if (round.isMultiround) {
        dispatch(deleteGameRound({ gameId: gameId, roundId: round.roundId }))
        return
      }

      dispatch(deleteGame(gameId))
    }

    if (value === GAMEOPTIONS.Logs) {
      setLogsModalOpen(true)
      dispatch(getGameLogs({ gameId: gameId }))
    }

    return
  }

  const getUpdateGamePayload = (isCompleted: boolean) => {
    return {
      id: round.eventId,
      data: {
        rounds: [
          {
            id: String(round.roundId),
            isCompleted: isCompleted,
          },
        ],
      },
    }
  }

  const getRoundOptions = (round: GameSearchRound) => {
    const isTmGame = roundIsTmGame(round)
    if (isTmGame) {
      return tmGameOptions(round)
    }
    return appGameOptions(round)
  }

  const getRoundCreator = (round: GameSearchRound) => {
    const { creator } = round
    const avatar = creator?.avatarUrl?.includes('://') ? creator?.avatarUrl : `${S3_URL}${creator?.avatarUrl}`
    const avatarUrl = creator?.avatarUrl ? avatar : ''
    const firstName = creator?.firstName ? `${creator.firstName} ` : ''
    const lastName = creator?.lastName || ''
    const creatorName = `${firstName}${lastName}`
    return (
      <CreatorImage
        src={avatarUrl}
        alt={creatorName}
        title={creatorName}
        onError={(e: any) => (e.target.src = DEFAULT_USER_AVATAR)}
      />
    )
  }

  const getRoundCodes = (round: GameSearchRound) => {
    const joinCode = round.parent?.gameCode || round.gameCode || '-'
    const viewCode = round.parent?.viewCode || round.viewCode || '-'
    return <GameCodes>{`${joinCode} / ${viewCode}`}</GameCodes>
  }

  const isDeletingOrUpdatingRound = (round: GameSearchRound) => {
    if (round.isMultiround) {
      return deletingStatus === 'loading' && deletingId === round.roundId
    }
    return [deletingStatus, updatingStatus].includes('loading') && [deletingId, updatingId].includes(round.eventId)
  }

  const clearLogs = () => {
    dispatch(clearGameLogs())
    setLogsModalOpen(false)
  }

  if (redirectTo) {
    return <Navigate to={redirectTo} />
  }

  return (
    <>
      <TableRow hover>
        <TableCell>{getRoundDate(round)}</TableCell>
        <TableCell>
          <LeaderboardLink
            to={ClientRoute.ADMIN_GAME_LEADERBOARD(round.eventId)}
            target="_blank"
            className={roundIsChildEvent(round) ? 'disabled' : ''}
          >
            {getRoundName(round)}
          </LeaderboardLink>
        </TableCell>
        <TableCell>{round.clubName}</TableCell>
        <TableCell>{round.courseName}</TableCell>
        <TableCell>
          {getRoundOptions(round)}
          {isDeletingOrUpdatingRound(round) && <LoadingSpinnerGreen />}
          {deletingStatus === 'failed' && deletingError && deletingId === round.eventId && (
            <ErrorMessage>{deletingError}</ErrorMessage>
          )}
          {updatingStatus === 'failed' && updatingError && updatingId === round.eventId && (
            <ErrorMessage>{updatingError}</ErrorMessage>
          )}
        </TableCell>
        <TableCell>{getRoundCreator(round)}</TableCell>
        {!user && <TableCell>{round.eventId}</TableCell>}
        {!user && <TableCell>{round.roundId}</TableCell>}
        <TableCell>{getRoundCodes(round)}</TableCell>
      </TableRow>
      {playersModalOpen && (
        <SingleRoundPlayersModal
          open={playersModalOpen}
          close={() => setPlayersModalOpen(false)}
          round={round}
          user={user}
        />
      )}
      {logsModalOpen && <GameLogsModal open={logsModalOpen} close={clearLogs} />}
    </>
  )
}

const CreatorImage = styled.img`
  width: 30px;
  height: 30px;
  border-radius: 50%;
`

const SimulatorBadge = styled(SimulatorIcon)`
  height: 32px;
  width: 32px;
  margin-left: 10px;
`

const Badge = styled.span`
  color: #fff;
  border-radius: 2px;
  font-weight: bold;
  font-size: 10px;
  line-height: 14px;
  text-align: center;
  padding: 2px 5px;
  margin-left: 5px;
`

const TmBadge = styled(Badge)`
  background-color: ${(props) => props.theme.palette.primary.main};
`

const HcpBadge = styled(Badge)`
  background-color: #0863bc;
`

const RvbBadge = styled(Badge)`
  background-color: #0d2044;
`

const PrivateBadge = styled(Badge)`
  background-color: #fcc200;
`

const GameCodes = styled.span`
  font-size: 12px;
`

const TmLinkButton = styled.a`
  padding: 10px;
  border: 1px solid;
  border-color: ${(props) => props.theme.palette.primary.main};
  background-color: ${(props) => props.theme.customPalette.light};
  color: ${(props) => props.theme.palette.primary.main};
  text-decoration: none;
  display: block;
  text-align: center;
  max-width: 80px;
`

const GameDropdown = styled.select`
  width: 125px;
`

const LeaderboardLink = styled(Link)`
  display: flex;
  align-items: center;
  color: ${(props) => props.theme.palette.primary.main};
  text-decoration: none;

  &.disabled {
    pointer-events: none;
    color: rgba(0, 0, 0, 0.87);
  }
`
